<!--
/**
 * OrangeHRM is a comprehensive Human Resource Management (HRM) System that captures
 * all the essential functionalities required for any enterprise.
 * Copyright (C) 2006 OrangeHRM Inc., http://www.orangehrm.com
 *
 * OrangeHRM is free software: you can redistribute it and/or modify it under the terms of
 * the GNU General Public License as published by the Free Software Foundation, either
 * version 3 of the License, or (at your option) any later version.
 *
 * OrangeHRM is distributed in the hope that it will be useful, but WITHOUT ANY WARRANTY;
 * without even the implied warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.
 * See the GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License along with OrangeHRM.
 * If not, see <https://www.gnu.org/licenses/>.
 */
 -->

<template>
  <oxd-divider class="orangehrm-horizontal-margin" />
  <div class="orangehrm-horizontal-padding orangehrm-vertical-padding">
    <div class="orangehrm-action-header">
      <oxd-text tag="h6" class="orangehrm-main-title">
        Payments History
      </oxd-text>
      <oxd-button
        v-if="canEdit"
        :label="$t('general.add')"
        icon-name="plus"
        display-type="ghost"
        @click="onClickAdd"
      />
    </div>
  </div>
  <table-header
    :total="total"
    :loading="isLoading"
    :selected="checkedItems.length"
    @delete="onClickDeleteSelected"
  />
  <div class="orangehrm-container">
    <oxd-card-table
      v-model:selected="checkedItems"
      :items="items.data"
      :headers="tableHeaders"
      :selectable="canEdit"
      :clickable="false"
      :loading="isLoading"
      row-decorator="oxd-table-decorator-card"
    />
  </div>
  <div class="orangehrm-bottom-container">
    <oxd-text> Total Paid: ${{ totalAmount }} </oxd-text>
  </div>
  <add-invoicepayment-modal
    v-if="showAddInvoicePaymentModal"
    :custName="customerName"
    :custId="customerId"
    :invTotal="invoiceTotal"
    :invNumber="invoiceNumber"
    :invDate="invoiceDate"
    @close="onCloseAddInvoicePaymentModal"
  ></add-invoicepayment-modal>
  <edit-invoicepayment-modal
    v-if="showEditInvoicePaymentModal"
    :invNumber="invoiceNumber"
    :custId="customerId"
    :custName="customerName"
    :invDate="invoiceDate"
    :data="editModalState"
    @close="onCloseEditInvoicePaymentModal"
  ></edit-invoicepayment-modal>
  <delete-confirmation ref="deleteDialog"></delete-confirmation>
</template>

<script>
import {APIService} from '@/core/util/services/api.service';
import usePaginate from '@ohrm/core/util/composable/usePaginate';
import DeleteConfirmationDialog from '@ohrm/components/dialogs/DeleteConfirmationDialog.vue';
import {computed, toRefs} from 'vue';
import AddInvoicePaymentModal from './AddInvoicePaymentModal.vue';
import EditInvoicePaymentModal from './EditInvoicePaymentModal.vue';
import useLocale from '@/core/util/composable/useLocale';
import {formatDate, parseDate} from '@ohrm/core/util/helper/datefns';
import useDateFormat from '@/core/util/composable/useDateFormat';

export default {
  name: 'InvoicePayments',

  props: {
    custName: {
      type: String,
      required: true,
    },
    invTotal: {
      type: String,
      required: true,
    },
    invNumber: {
      type: String,
      required: true,
    },
    invDate: {
      type: String,
      required: true,
    },
    custId: {
      type: String,
      required: true,
    },
  },

  components: {
    'delete-confirmation': DeleteConfirmationDialog,
    'add-invoicepayment-modal': AddInvoicePaymentModal,
    'edit-invoicepayment-modal': EditInvoicePaymentModal,
  },

  setup(props) {
    const {locale} = useLocale();
    const {jsDateFormat} = useDateFormat();

    const {custName: customerName} = toRefs(props);
    const {custId: customerId} = toRefs(props);
    const {invNumber: invoiceNumber} = toRefs(props);
    const {invDate: invoiceDate} = toRefs(props);
    const {invTotal: invoiceTotal} = toRefs(props);

    const http = new APIService(
      window.appGlobal.baseUrl,
      `/api/v2/pim/invoicepayments/` + props.custId + '/' + props.invNumber,
    );

    const invoicepaymentDataNormalizer = (data) => {
      return data.map((item) => {
        return {
          id: item.id,
          datePaid: item.datePaid.date.split(' ')[0],
          amount: item.amount ? item.amount.toFixed(2) : '0.00',
          note: item.note ? item.note : '',
          invoiceNumber: item.invoiceNumber,
          invoiceDate: item.invoiceDate,
          customerId: item.customerId,
        };
      });
    };

    const {
      showPaginator,
      currentPage,
      total,
      pages,
      pageSize,
      response,
      isLoading,
      execQuery,
    } = usePaginate(http, {
      normalizer: invoicepaymentDataNormalizer,
      toastNoRecords: false,
    });

    const totalAmount = computed(() => {
      const meta = response.value?.meta;
      return meta ? meta.totalAmount.toFixed(2) : (0.0).toFixed(2);
    });

    return {
      customerName,
      customerId,
      invoiceNumber,
      invoiceDate,
      invoiceTotal,
      http,
      showPaginator,
      currentPage,
      isLoading,
      total,
      pages,
      pageSize,
      execQuery,
      items: response,
      totalAmount,
      canEdit: true,
    };
  },

  data() {
    return {
      headers: [
        {
          name: 'invoiceNumber',
          slot: 'title',
          title: 'Invoice Number',
          style: {flex: 2},
        },
        {
          name: 'datePaid',
          title: 'Date Paid',
          style: {flex: 1},
        },
        {
          name: 'note',
          title: this.$t('general.description'),
          cellType: 'oxd-table-cell-truncate',
          style: {flex: 2},
        },
        {
          name: 'amount',
          style: {flex: 1},
          title: this.$t('general.amount'),
        },
      ],
      checkedItems: [],
      showAddInvoicePaymentModal: false,
      showEditInvoicePaymentModal: false,
      editModalState: null,
    };
  },

  computed: {
    tableHeaders() {
      let computedHeaders = this.headers;
      const headerActions = {
        name: 'actions',
        slot: 'action',
        title: this.$t('general.actions'),
        style: {flex: 1},
        cellType: 'oxd-table-cell-actions',
        cellConfig: {},
      };
      if (this.canEdit) {
        headerActions.cellConfig.edit = {
          onClick: this.onClickEdit,
          props: {
            name: 'pencil-fill',
          },
        };
        headerActions.cellConfig.delete = {
          onClick: this.onClickDelete,
          component: 'oxd-icon-button',
          props: {
            name: 'trash',
          },
        };
      }

      computedHeaders[3] = {
        name: 'amount',
        title: `${this.$t('general.amount')}`,
        style: {flex: 1},
      };
      if (Object.keys(headerActions.cellConfig).length > 0) {
        computedHeaders.push(headerActions);
      }

      return computedHeaders;
    },
    formatedAmount() {
      const amount = Number(this.totalAmount);
      return amount.toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },
  },

  methods: {
    async resetDataTable() {
      this.checkedItems = [];
      await this.execQuery();
    },
    onClickAdd() {
      this.showAddInvoicePaymentModal = true;
    },
    onClickDeleteSelected() {
      const ids = [];
      this.checkedItems.forEach((index) => {
        ids.push(this.items?.data[index].id);
      });
      this.$refs.deleteDialog.showDialog().then((confirmation) => {
        if (confirmation === 'ok') {
          this.deleteItems(ids);
        }
      });
    },
    deleteItems(items) {
      if (items instanceof Array) {
        this.isLoading = true;
        this.http
          .deleteAll({
            ids: items,
          })
          .then(() => {
            return this.$toast.deleteSuccess();
          })
          .then(() => {
            this.isLoading = false;
            this.resetDataTable();
          });
      }
    },
    onClickDelete(item) {
      this.$refs.deleteDialog.showDialog().then((confirmation) => {
        if (confirmation === 'ok') {
          this.deleteItems([item.id]);
        }
      });
    },
    onCloseAddInvoicePaymentModal() {
      this.showAddInvoicePaymentModal = false;
      this.resetDataTable();
    },
    onCloseEditInvoicePaymentModal() {
      this.showEditInvoicePaymentModal = false;
      this.resetDataTable();
    },
    onClickEdit(item) {
      this.showEditInvoicePaymentModal = true;
      this.editModalState = item;
      this.showAddInvoicePaymentModal = false;
    },
  },
};
</script>

<style scoped lang="scss">
.oxd-divider {
  margin-top: 0;
  margin-bottom: 0;
}
.orangehrm-attachment {
  border-bottom-right-radius: 1.2rem;
  overflow: hidden;
}
.orangehrm-action {
  &-header {
    display: flex;
    overflow-wrap: break-word;
    align-items: center;
    button {
      margin-left: 1rem;
      white-space: nowrap;
    }
  }
}
</style>
